// src/Presentation/Components/Forms/CreateOrEditDepartmentModalForm.tsx
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import styles from "./Forms.module.css";
import TextInput from "../UILibrary/TextInput";
import Submission from "../UILibrary/Submission";
import { defaultValuesModalDepartment } from "Utils/Global/globalConstants";
import {
    ICreateDepartmentFormData,
    ICreateOrEditDepartmentFormProps,
} from "@model/Department.model";
import { DepartmentsService } from "../../../Logic/DepartmentsService";
import { UserAuthenticationStoreImplementation } from "Data/UserAuthentication/UserAuthenticationStoreImplementation";
import { useForceLogoutAndRedirect } from "../../../Logic/Functions/Hooks/useForceLogoutAndRedirect";
import { UnauthorizedPopupStoreImplementation } from "Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation";
import { useNavigate } from "react-router-dom";

const NAME_MAX = 50;
const DESC_MAX = 500;

const CreateOrEditDepartmentModalForm: React.FC<
    ICreateOrEditDepartmentFormProps
> = ({ onClose, forEdit = false, initialDepartment, onSuccess }) => {
    const { control, handleSubmit, reset } = useForm<ICreateDepartmentFormData>({
        mode: "onSubmit",
        defaultValues: defaultValuesModalDepartment,
    });

    const service = new DepartmentsService();
    const [apiError, setApiError] = useState<string | undefined>(undefined);
    const [submitting, setSubmitting] = useState(false);
    const { showPopup } = UnauthorizedPopupStoreImplementation();
    const navigate = useNavigate();
    const onAuthError = useForceLogoutAndRedirect();
    const authStore = UserAuthenticationStoreImplementation();

    useEffect(() => {
        const defaults = service.getFormDefaultsFromDepartment({
            initialDepartment,
            defaultValues: defaultValuesModalDepartment,
        });

        reset(defaults);
    }, [initialDepartment, reset]);

    const onValidSubmit = async (values: ICreateDepartmentFormData) => {
        const token = authStore.authenticationState?.token ?? "";

        await service.handleCreateOrSaveDepartment({
            isEdit: forEdit,
            initialDepartment: initialDepartment ?? null,
            values,
            token,
            onAuthError,
            setLoading: setSubmitting,
            setApiError: (m) => setApiError(m ?? undefined),
            reset,
            onSuccess,
            onClose,
            onForbidden: (msg) => {
                showPopup(msg);
                navigate("/", { replace: true });
            },

        });
    };

    return (
        <form
            className={styles.mcdForm}
            data-testid="create-dept-form"
            onSubmit={handleSubmit(onValidSubmit)}
        >
            <Controller
                name="name"
                control={control}
                rules={{
                    required: "Name is required",
                    maxLength: {
                        value: NAME_MAX,
                        message: `Name cannot exceed ${NAME_MAX} characters`,
                    },
                }}
                render={({ field, fieldState }) => (
                    <div className={styles.mcdFieldGroup}>
                        <TextInput
                            label="Name"
                            placeholder="e.g. Engineering"
                            value={field.value}
                            onChange={(val) => {
                                const next =
                                    val.length <= NAME_MAX ? val : val.slice(0, NAME_MAX);
                                field.onChange(next);
                            }}
                            disabled={submitting}
                            error={fieldState.error?.message}
                        />
                    </div>
                )}
            />

            <Controller
                name="description"
                control={control}
                rules={{
                    required: "Description is required",
                    maxLength: {
                        value: DESC_MAX,
                        message: `Description cannot exceed ${DESC_MAX} characters`,
                    },
                }}
                render={({ field, fieldState }) => (
                    <div
                        className={styles.mcdFieldGroup}
                        data-testid="create-dept-desc-group"
                    >
                        <label className={styles.mcdInputLabel} htmlFor="cdm-desc">
                            Description
                        </label>
                        <textarea
                            id="cdm-desc"
                            className={
                                styles.mcdTextarea +
                                " " +
                                (fieldState.error ? styles.mdcinputWithError : "")
                            }
                            maxLength={DESC_MAX}
                            rows={3}
                            value={field.value || ""}
                            onChange={field.onChange}
                            placeholder="Describe this department…"
                            disabled={submitting}
                            data-testid="create-dept-desc"
                        />
                        {fieldState.error?.message && (
                            <span
                                className={styles.mcdErrorText}
                                data-testid="create-dept-desc-error"
                            >
                                {fieldState.error.message}
                            </span>
                        )}
                    </div>
                )}
            />

            <Controller
                name="headOfDepartmentUserId"
                control={control}
                rules={{
                    required: "Head of department is required",
                }}
                render={({ field, fieldState }) => (
                    <div className={styles.mcdFieldGroup}>
                        <TextInput
                            label="Head of Department (User ID)"
                            placeholder="User UUID…"
                            value={field.value || ""}
                            onChange={field.onChange}
                            disabled={submitting}
                            error={fieldState.error?.message}
                        />
                    </div>
                )}
            />

            <div className={styles.mcdActions}>
                <button
                    type="button"
                    className={styles.mcdBtnGhost}
                    onClick={onClose}
                    disabled={submitting}
                    data-testid="create-dept-cancel"
                >
                    Cancel
                </button>

                <Submission
                    buttonText={
                        submitting
                            ? forEdit
                                ? "Saving…"
                                : "Creating…"
                            : forEdit
                                ? "Save"
                                : "Create"
                    }
                    disabled={submitting}
                    error={apiError}
                    className={styles.mcdBtnPrimary}
                />
            </div>
        </form>
    );
};

export default CreateOrEditDepartmentModalForm;
